import React, { Component } from "react";
import { injectIntl } from "react-intl";
import { Row, Card, CardBody, Input, Button, Badge, Alert } from "reactstrap";
import { Colxx } from "../../../components/common/CustomBootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import axios from "axios";
import moment from "moment";
import createSocket from "../../../helpers/createSocket";
import { END_POINT, END_POINT_SOCKET } from "../../../constants/defaultValues";
import IntlMessages from "../../../helpers/IntlMessages";

class FacebookCommentApp extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pages: [],
      loadingPages: true,
      selectedPageFilter: "",
      // Posts
      posts: [],
      loadingPosts: false,
      selectedPost: null,
      // Comments
      comments: [],
      loadingComments: false,
      // Reply
      replyText: "",
      replyingTo: null, // comment_id being replied to
      sendingReply: false,
      editingCommentId: null,
      editingMessage: "",
      // Sync
      syncingPosts: false,
      // Status Filters
      interactionFilter: "all", // all, unread, unreplied
      // Errors
      success: null,
      // Manual Token States
      manualToken: "",
      previewPages: [],
      selectedPageIds: new Set(),
      previewLoading: false,
      connectingManualToken: false,
      socialError: null,
      socialSuccess: null,
    };
    this.socket = createSocket();
  }

  componentDidMount() {
    this.loadPages();

    // Listen for real-time comment events
    this.socket.on("facebook_comment", (data) => {
      // console.log("[FB Comment] Real-time event:", data);

      // Only react if the event is for the currently selected page
      if (
        this.state.selectedPageFilter &&
        data.page_id !== this.state.selectedPageFilter
      ) {
        return;
      }
      if (
        this.state.selectedPost &&
        data.post_id === this.state.selectedPost.post_id
      ) {
        this.loadComments(data.post_id);
      }
      // Refresh post list to update comment counts
      if (this.state.selectedPageFilter) {
        this.loadPosts(this.state.selectedPageFilter);
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.activeTab === "fb-comments" &&
      prevProps.activeTab !== "fb-comments" &&
      this.state.pages.length === 0 &&
      !this.state.loadingPages
    ) {
      this.loadPages();
    }
  }

  componentWillUnmount() {
    if (this.socket) {
      this.socket.off("facebook_comment");
      this.socket.disconnect();
    }
  }

  loadPages = async () => {
    this.setState({ loadingPages: true });
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${END_POINT}/api/facebook/pages`, {
        params: { user_id },
        headers: { Authorization: `Bearer ${token}` },
      });
      const pages = res.data.data || [];
      this.setState({ pages, loadingPages: false });
      if (pages.length > 0) {
        this.setState({ selectedPageFilter: pages[0].page_id }, () => {
          this.loadPosts(pages[0].page_id);
          this.handleSyncPosts(); // Auto sync on init
        });
      }
    } catch (err) {
      this.setState({ loadingPages: false });
    }
  };

  loadPosts = async (pageId, preserveSelection = false) => {
    if (!preserveSelection) {
      this.setState({ loadingPosts: true, selectedPost: null, comments: [] });
    } else {
      this.setState({ loadingPosts: true });
    }
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      const res = await axios.get(`${END_POINT}/api/facebook/comments/posts`, {
        params: { page_id: pageId, user_id },
        headers: { Authorization: `Bearer ${token}` },
      });
      const newPosts = res.data.data || [];
      this.setState({ posts: newPosts, loadingPosts: false });

      // If preserving selection, update the selectedPost object from the fresh data
      if (preserveSelection && this.state.selectedPost) {
        const updatedSelectedPost = newPosts.find(p => p.post_id === this.state.selectedPost.post_id);
        if (updatedSelectedPost) {
          this.setState({ selectedPost: updatedSelectedPost });
          this.loadComments(updatedSelectedPost.post_id);
        }
      }
    } catch (err) {
      this.setState({ posts: [], loadingPosts: false });
    }
  };

  handleReadComment = async (commentId) => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/comments/read/${commentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // Update local state to avoid full reload
      this.setState({
        comments: this.state.comments.map((c) =>
          c.comment_id === commentId ? { ...c, is_read: true } : c,
        ),
      });
    } catch (err) {
      console.error("Error marking comment as read:", err);
    }
  };

  handleManualTokenChange = (e) => {
    this.setState({ manualToken: e.target.value, socialError: null });
  };

  handlePreviewPages = async () => {
    const { manualToken } = this.state;
    if (!manualToken.trim()) return;

    this.setState({
      previewLoading: true,
      socialError: null,
      previewPages: [],
    });
    try {
      const res = await axios.post(`${END_POINT}/api/facebook/preview-pages`, {
        access_token: manualToken.trim(),
      });
      const pages = res.data.data || [];
      this.setState({
        previewPages: pages,
        previewLoading: false,
        selectedPageIds: new Set(pages.map((p) => p.page_id)), // Select all by default
      });
    } catch (err) {
      this.setState({
        previewLoading: false,
        socialError: err.response?.data?.error || "Không thể kiểm tra Token",
      });
    }
  };

  togglePageSelection = (pageId) => {
    this.setState((prevState) => {
      const newSelected = new Set(prevState.selectedPageIds);
      if (newSelected.has(pageId)) {
        newSelected.delete(pageId);
      } else {
        newSelected.add(pageId);
      }
      return { selectedPageIds: newSelected };
    });
  };

  toggleSelectAll = () => {
    this.setState((prevState) => {
      const { previewPages, selectedPageIds } = prevState;
      if (selectedPageIds.size === previewPages.length) {
        return { selectedPageIds: new Set() };
      } else {
        return { selectedPageIds: new Set(previewPages.map((p) => p.page_id)) };
      }
    });
  };

  handleConnectSelectedPages = async () => {
    const { manualToken, previewPages, selectedPageIds } = this.state;
    const pagesToConnect = previewPages.filter((p) =>
      selectedPageIds.has(p.page_id),
    );

    if (pagesToConnect.length === 0) {
      this.setState({ socialError: "Vui lòng chọn ít nhất 1 Fanpage" });
      return;
    }

    this.setState({ connectingManualToken: true, socialError: null });
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/connect-manual`,
        {
          user_id,
          pages: pagesToConnect.map((p) => ({
            ...p,
            access_token: manualToken.trim(), // Use the manual token for all selected pages
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      this.setState({
        connectingManualToken: false,
        manualToken: "",
        previewPages: [],
        selectedPageIds: new Set(),
        socialSuccess: "Kết nối Fanpage thành công!",
      });

      // Reload pages and load data
      this.loadPages();
    } catch (err) {
      this.setState({
        connectingManualToken: false,
        socialError: err.response?.data?.error || "Kết nối thất bại",
      });
    }
  };

  loadComments = async (postId) => {
    this.setState({ loadingComments: true });
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${END_POINT}/api/facebook/comments/thread/${postId}`,
        {
          params: { user_id },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      this.setState({ comments: res.data.data || [], loadingComments: false });
    } catch (err) {
      this.setState({ comments: [], loadingComments: false });
    }
  };

  handleSyncPosts = async () => {
    const { selectedPageFilter } = this.state;
    if (!selectedPageFilter) return;
    this.setState({ syncingPosts: true, error: null });
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/comments/sync-posts`,
        { page_id: selectedPageFilter, user_id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      this.loadPosts(selectedPageFilter, true);
      this.setState({
        syncingPosts: false,
      });
    } catch (err) {
      this.setState({
        syncingPosts: false,
        error: err.response?.data?.error || "Sync failed",
      });
    }
  };

  handleSelectPost = (post) => {
    this.setState({ selectedPost: post, replyingTo: null, replyText: "" });
    this.loadComments(post.post_id);
  };

  handleSelectComment = (comment) => {
    if (
      !comment.is_read &&
      comment.sender_id !== this.state.selectedPageFilter
    ) {
      this.handleReadComment(comment.comment_id);
    }
  };

  handlePageFilterChange = (e) => {
    const pageId = e.target.value;
    this.setState({ selectedPageFilter: pageId }, () => {
      if (pageId) {
        this.loadPosts(pageId);
        this.handleSyncPosts(); // Auto sync when page changes
      }
    });
  };

  handleReply = async (commentId) => {
    const { replyText, selectedPost, selectedPageFilter } = this.state;
    if (!replyText.trim()) return;
    this.setState({ sendingReply: true });
    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/comments/reply`,
        {
          page_id: selectedPageFilter,
          comment_id: commentId,
          post_id: selectedPost.post_id,
          message: replyText.trim(),
          user_id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      this.setState({ sendingReply: false, replyText: "", replyingTo: null });
      this.loadComments(selectedPost.post_id);
    } catch (err) {
      this.setState({
        sendingReply: false,
        error: err.response?.data?.error || "Reply failed",
      });
    }
  };

  handleHideComment = async (commentId, isHidden) => {
    const originalComments = this.state.comments;
    // Optimistic UI update
    const updatedComments = this.state.comments.map((c) => {
      if (c.comment_id === commentId) return { ...c, is_hidden: !isHidden };
      return c;
    });
    this.setState({ comments: updatedComments });

    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/comments/hide`,
        {
          page_id: this.state.selectedPageFilter,
          comment_id: commentId,
          is_hidden: !isHidden,
          user_id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      this.setState({
        error: err.response?.data?.error || this.props.intl.formatMessage({ id: "fb.comment.error-hide" }),
        comments: originalComments, // Rollback to original
      });
    }
  };

  handleLikeComment = async (commentId) => {
    const originalComments = this.state.comments;
    // Optimistic UI update
    const updatedComments = this.state.comments.map((c) => {
      if (c.comment_id === commentId) return { ...c, is_liked: true };
      return c;
    });
    this.setState({ comments: updatedComments });

    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${END_POINT}/api/facebook/comments/react`,
        {
          page_id: this.state.selectedPageFilter,
          comment_id: commentId,
          user_id,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      this.setState({
        error: err.response?.data?.error || this.props.intl.formatMessage({ id: "fb.comment.error-like" }),
        comments: originalComments, // Rollback
      });
    }
  };

  handleDeleteComment = async (commentId) => {
    if (!window.confirm(this.props.intl.formatMessage({ id: "fb.comment.delete-confirm" }))) return;

    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.delete(`${END_POINT}/api/facebook/comments/${commentId}`, {
        data: {
          page_id: this.state.selectedPageFilter,
          user_id,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      this.setState({
        comments: this.state.comments.filter((c) => c.comment_id !== commentId),
      });
    } catch (err) {
      this.setState({ error: err.response?.data?.error || this.props.intl.formatMessage({ id: "fb.comment.error-delete" }) });
    }
  };

  handleUpdateComment = async () => {
    const { editingCommentId, editingMessage, selectedPost } = this.state;
    if (!editingMessage.trim()) return;

    try {
      const user_id = localStorage.getItem("b");
      const token = localStorage.getItem("access_token");
      await axios.put(`${END_POINT}/api/facebook/comments/${editingCommentId}`, {
        page_id: this.state.selectedPageFilter,
        message: editingMessage,
        user_id,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      this.setState({
        comments: this.state.comments.map((c) => 
          c.comment_id === editingCommentId ? { ...c, message: editingMessage } : c
        ),
        editingCommentId: null,
        editingMessage: "",
      });
    } catch (err) {
      this.setState({ error: err.response?.data?.error || this.props.intl.formatMessage({ id: "fb.comment.error-edit" }) });
    }
  };

  renderCommentTree(comments) {
    // Build tree structure
    const rootComments = comments.filter((c) => !c.parent_id);
    const replies = comments.filter((c) => c.parent_id);

    const getReplies = (parentId) =>
      replies.filter((r) => r.parent_id === parentId);

    return rootComments.map((comment) => (
      <div key={comment.comment_id} className="mb-3">
        {this.renderSingleComment(comment, false)}
        {/* Nested Replies */}
        {getReplies(comment.comment_id).map((reply) => (
          <div key={reply.comment_id} className="ml-4 mt-2">
            {this.renderSingleComment(reply, true)}
          </div>
        ))}
        {/* Reply input */}
        {this.state.replyingTo === comment.comment_id && (
          <div className="ml-4 mt-2 d-flex" style={{ gap: 8 }}>
            <Input
              type="text"
              bsSize="sm"
              placeholder={this.props.intl.formatMessage({
                id: "fb.comment.reply-placeholder",
              })}
              value={this.state.replyText}
              onChange={(e) => this.setState({ replyText: e.target.value })}
              onKeyPress={(e) => {
                if (e.key === "Enter") this.handleReply(comment.comment_id);
              }}
              disabled={this.state.sendingReply}
            />
            <Button
              color="primary"
              size="sm"
              onClick={() => this.handleReply(comment.comment_id)}
              disabled={this.state.sendingReply || !this.state.replyText.trim()}
            >
              {this.state.sendingReply ? (
                <i className="simple-icon-refresh spin-animation" />
              ) : (
                <i className="simple-icon-paper-plane" />
              )}
            </Button>
          </div>
        )}
      </div>
    ));
  }

  renderSingleComment(comment, isReply) {
    const isHidden = comment.is_hidden;
    const isPageComment = this.state.pages.some(
      (p) => p.page_id === comment.sender_id,
    );

    return (
      <div
        className={`d-flex p-2 ${isHidden ? "opacity-50" : ""}`}
        style={{
          backgroundColor: isPageComment ? "#e8f4fd" : "#f8f9fa",
          border: isHidden ? "1px dashed #dee2e6" : "1px solid transparent",
          borderRadius: "15px",
          gap: 12,
          cursor: "pointer",
        }}
        onClick={() => this.handleSelectComment(comment)}
      >
        {/* Avatar */}
        <div className="mr-2 flex-shrink-0">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold"
            style={{
              width: 32,
              height: 32,
              fontSize: 13,
              backgroundColor: isPageComment ? "#1877f2" : "#6c757d",
            }}
          >
            {(comment.sender_name || "?").charAt(0).toUpperCase()}
          </div>
        </div>
        {/* Content */}
        <div className="flex-grow-1">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <strong style={{ fontSize: 13 }}>
                {comment.sender_name || this.props.intl.formatMessage({ id: "fb.comment.unknown" })}
              </strong>
              {isPageComment && (
                <Badge
                  color="primary"
                  pill
                  className="ml-1"
                  style={{ fontSize: 10 }}
                >
                  <IntlMessages id="fb.comment.page-badge" />
                </Badge>
              )}

              {isHidden && (
                <Badge
                  color="warning"
                  pill
                  className="ml-1"
                  style={{ fontSize: 10 }}
                >
                  <IntlMessages id="fb.comment.hidden" />
                </Badge>
              )}
            </div>
            <div className="d-flex align-items-center">
              {!comment.is_read && !isPageComment && (
                <div
                  className="bg-primary rounded-circle mr-2"
                  style={{ width: 8, height: 8 }}
                  title={this.props.intl.formatMessage({ id: "fb.comment.unread" })}
                />
              )}
              <small className="text-muted" style={{ fontSize: 11 }}>
                {moment(comment.created_time).fromNow()}
              </small>
            </div>
          </div>
          <div className="mb-1 mt-1" style={{ fontSize: 13 }}>
            {this.state.editingCommentId === comment.comment_id ? (
              <div className="d-flex" style={{ gap: 8 }}>
                <Input
                  type="text"
                  size="sm"
                  value={this.state.editingMessage}
                  onChange={(e) => this.setState({ editingMessage: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") this.handleUpdateComment();
                  }}
                />
                <Button color="primary" size="xs" onClick={this.handleUpdateComment}>
                  <i className="simple-icon-check" />
                </Button>
                <Button 
                  color="outline-secondary" 
                  size="xs" 
                  onClick={() => this.setState({ editingCommentId: null })}
                >
                  <i className="simple-icon-close" />
                </Button>
              </div>
            ) : (
              comment.message || <em className="text-muted">[Media]</em>
            )}
          </div>
          {/* Actions */}
          <div
            className="d-flex align-items-center"
            style={{ gap: 12, fontSize: 12 }}
          >
            <span
              className={`${comment.is_liked ? "text-primary font-weight-bold" : "text-muted"} cursor-pointer`}
              style={{ cursor: "pointer" }}
              onClick={() => this.handleLikeComment(comment.comment_id)}
            >
              <i className={`simple-icon-like mr-1 ${comment.is_liked ? "font-weight-bold" : ""}`} />
              <IntlMessages id="fb.comment.like" />
            </span>
            {!isReply && (
              <span
                className="text-muted cursor-pointer"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  this.setState({
                    replyingTo:
                      this.state.replyingTo === comment.comment_id
                        ? null
                        : comment.comment_id,
                    replyText: "",
                  })
                }
              >
                <i className="simple-icon-action-undo mr-1" />
                <IntlMessages id="fb.comment.reply" />
              </span>
            )}

            {!isPageComment ? (
              <span
                className={`cursor-pointer ${isHidden ? "text-success" : "text-warning"}`}
                style={{ cursor: "pointer" }}
                onClick={() =>
                  this.handleHideComment(comment.comment_id, isHidden)
                }
              >
                <i className={`simple-icon-${isHidden ? "eye" : "ban"} mr-1`} />
                {isHidden ? (
                  <IntlMessages id="fb.comment.unhide" />
                ) : (
                  <IntlMessages id="fb.comment.hide" />
                )}
              </span>
            ) : (
              <>
                <span
                  className="text-muted cursor-pointer"
                  style={{ cursor: "pointer" }}
                  onClick={() => this.setState({ editingCommentId: comment.comment_id, editingMessage: comment.message })}
                >
                  <i className="simple-icon-pencil mr-1" />
                  <IntlMessages id="fb.comment.edit" />
                </span>
                <span
                  className="text-danger cursor-pointer"
                  style={{ cursor: "pointer" }}
                  onClick={() => this.handleDeleteComment(comment.comment_id)}
                >
                  <i className="simple-icon-trash mr-1" />
                  <IntlMessages id="fb.comment.delete" />
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      pages,
      loadingPages,
      selectedPageFilter,
      posts,
      loadingPosts,
      selectedPost,
      comments,
      loadingComments,
      syncingPosts,
      error,
      success,
    } = this.state;

    // Empty state - no pages connected (show manual connection)
    if (!loadingPages && pages.length === 0) {
      const {
        manualToken,
        previewPages,
        selectedPageIds,
        previewLoading,
        connectingManualToken,
        socialError,
        socialSuccess,
      } = this.state;

      return (
        <Row className="pr-4">
          <Colxx xxs="12">
            <Card className="h-100 mb-4" style={{ minHeight: "75vh" }}>
              <CardBody className="d-flex flex-column justify-content-center align-items-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mb-4"
                  style={{
                    width: 70,
                    height: 70,
                    backgroundColor: "rgba(24, 119, 242, 0.1)",
                  }}
                >
                  <i
                    className="iconsminds-facebook"
                    style={{ color: "#1877f2", fontSize: 36 }}
                  />
                </div>

                <h4 className="font-weight-bold mb-2">
                  <IntlMessages id="facebook.manual.title" />
                </h4>
                <p className="text-muted mb-4" style={{ maxWidth: 500 }}>
                  <IntlMessages id="fb.comment.no-pages-desc" />
                </p>

                <div className="w-100" style={{ maxWidth: 600 }}>
                  {socialError && (
                    <Alert color="danger" className="rounded">
                      {socialError}
                    </Alert>
                  )}
                  {socialSuccess && (
                    <Alert color="success" className="rounded">
                      {socialSuccess}
                    </Alert>
                  )}

                  <div className="d-flex mb-3" style={{ gap: "10px" }}>
                    <Input
                      type="textarea"
                      placeholder={this.props.intl.formatMessage({
                        id: "facebook.manual.placeholder",
                      })}
                      value={manualToken}
                      onChange={this.handleManualTokenChange}
                      rows={2}
                      className="rounded"
                      style={{ fontFamily: "monospace" }}
                      disabled={previewLoading}
                    />
                    <Button
                      color="info"
                      onClick={this.handlePreviewPages}
                      disabled={previewLoading || !manualToken.trim()}
                      style={{ minWidth: "120px" }}
                    >
                      {previewLoading ? (
                        <span>
                          <i className="simple-icon-refresh spin-animation mr-1" />
                          <IntlMessages id="facebook.manual.checking" />
                        </span>
                      ) : (
                        <span>
                          <i className="simple-icon-magnifier mr-1" />
                          <IntlMessages id="facebook.manual.check-btn" />
                        </span>
                      )}
                    </Button>
                  </div>

                  {previewPages.length > 0 && (
                    <div className="mt-3 text-left w-100">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 font-weight-bold ml-1">
                          <IntlMessages id="facebook.manual.select-pages" />
                          <span className="badge badge-primary ml-2">
                            {previewPages.length}
                          </span>
                        </h6>
                        {previewPages.length > 1 && (
                          <Button
                            color="link"
                            size="sm"
                            onClick={this.toggleSelectAll}
                            className="p-0"
                          >
                            {selectedPageIds.size === previewPages.length
                              ? this.props.intl.formatMessage({
                                  id: "facebook.manual.deselect-all",
                                })
                              : this.props.intl.formatMessage({
                                  id: "facebook.manual.select-all",
                                })}
                          </Button>
                        )}
                      </div>

                      <div
                        className="border rounded mb-3"
                        style={{
                          maxHeight: "200px",
                          overflowY: "auto",
                          backgroundColor: "#fff",
                        }}
                      >
                        {previewPages.map((page) => (
                          <div
                            key={page.page_id}
                            className="d-flex align-items-center p-2 border-bottom"
                            style={{
                              cursor: "pointer",
                              backgroundColor: selectedPageIds.has(page.page_id)
                                ? "#e8f4fd"
                                : "transparent",
                            }}
                            onClick={() =>
                              this.togglePageSelection(page.page_id)
                            }
                          >
                            <input
                              type="checkbox"
                              checked={selectedPageIds.has(page.page_id)}
                              onChange={() => {}}
                              className="mr-3 ml-2"
                              style={{
                                transform: "scale(1.2)",
                                cursor: "pointer",
                              }}
                            />
                            <div className="flex-grow-1">
                              <strong style={{ fontSize: "14px" }}>
                                {page.page_name}
                              </strong>
                              <div
                                className="text-muted"
                                style={{ fontSize: "12px" }}
                              >
                                ID: {page.page_id}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Button
                        color="success"
                        block
                        onClick={this.handleConnectSelectedPages}
                        disabled={
                          connectingManualToken || selectedPageIds.size === 0
                        }
                        className="py-2 font-weight-bold"
                      >
                        {connectingManualToken ? (
                          <span>
                            <i className="simple-icon-refresh spin-animation mr-1" />
                            <IntlMessages id="facebook.manual.connecting" />
                          </span>
                        ) : (
                          <span>
                            <i className="simple-icon-check mr-1" />
                            <IntlMessages id="facebook.manual.connect-btn" />
                          </span>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </Colxx>
        </Row>
      );
    }

    return (
      <Row className="pr-4">
        {/* Left: Post list */}
        <Colxx xxs="12" md="5" lg="4">
          <Card className="h-100">
            <CardBody
              className="p-0 d-flex flex-column"
              style={{ height: "75vh" }}
            >
              {/* Page filter + Sync */}
              <div className="p-2 border-bottom">
                {pages.length > 1 && (
                  <Input
                    type="select"
                    value={selectedPageFilter}
                    onChange={this.handlePageFilterChange}
                    bsSize="sm"
                  >
                    {pages.map((p) => (
                      <option key={p.page_id} value={p.page_id}>
                        {p.page_name}
                      </option>
                    ))}
                  </Input>
                )}

                {/* Interaction Filters */}
                <div className="d-flex mt-2 mb-2" style={{ gap: 5 }}>
                  <Button
                    color={
                      this.state.interactionFilter === "all"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="xs"
                    onClick={() => this.setState({ interactionFilter: "all" })}
                    className="flex-grow-1"
                  >
                    <IntlMessages id="fb.comment.filter-all" />
                  </Button>
                  <Button
                    color={
                      this.state.interactionFilter === "unread"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="xs"
                    onClick={() =>
                      this.setState({ interactionFilter: "unread" })
                    }
                    className="flex-grow-1"
                  >
                    <IntlMessages id="fb.comment.filter-unread" />
                  </Button>
                  <Button
                    color={
                      this.state.interactionFilter === "unreplied"
                        ? "primary"
                        : "outline-secondary"
                    }
                    size="xs"
                    onClick={() =>
                      this.setState({ interactionFilter: "unreplied" })
                    }
                    className="flex-grow-1"
                  >
                    <IntlMessages id="fb.comment.filter-unreplied" />
                  </Button>
                </div>
              </div>

              {error && (
                <Alert
                  color="danger"
                  className="m-2 mb-0 py-1 px-2"
                  style={{ fontSize: 12 }}
                >
                  {error}
                  <button
                    className="close p-0 ml-2"
                    onClick={() => this.setState({ error: null })}
                  >
                    &times;
                  </button>
                </Alert>
              )}

              {/* Posts list */}
              <div className="flex-grow-1 position-relative overflow-hidden">
                {loadingPosts ? (
                  <div className="d-flex flex-column h-100 align-items-center justify-content-center">
                    <div
                      className="loading position-relative"
                      style={{ height: 20, width: 20, margin: "0 auto" }}
                    />
                    <p className="text-muted mt-3" style={{ fontSize: 13 }}>
                      <IntlMessages id="fb.comment.loading-posts" />
                    </p>
                  </div>
                ) : posts.length === 0 ? (
                  <div className="d-flex flex-column h-100 align-items-center justify-content-center text-muted p-4 text-center">
                    <i
                      className="simple-icon-note mb-3"
                      style={{ fontSize: 40, opacity: 0.4 }}
                    />
                    <p>
                      <IntlMessages id="fb.comment.no-posts" />
                    </p>
                    <small>
                      <IntlMessages id="fb.comment.no-posts-hint" />
                    </small>
                  </div>
                ) : (
                  <PerfectScrollbar
                    options={{ suppressScrollX: true, wheelPropagation: false }}
                    style={{ height: "100%" }}
                  >
                    <div className="pt-1">
                      {posts.map((post) => {
                        const isSelected =
                          selectedPost && selectedPost.post_id === post.post_id;
                        const previewText = post.message
                          ? post.message.length > 150
                            ? post.message.substring(0, 150) + "..."
                            : post.message
                          : "[Media Post]";

                        // Filter logic for POSTS
                        if (
                          this.state.interactionFilter === "unread" &&
                          Number(post.unread_count || 0) === 0
                        )
                          return null;
                        if (
                          this.state.interactionFilter === "unreplied" &&
                          Number(post.unreplied_count || 0) === 0
                        )
                          return null;

                        return (
                          <div
                            key={post.post_id}
                            className={`p-3 cursor-pointer ${isSelected ? "bg-light" : ""}`}
                            style={{
                              cursor: "pointer",
                              borderLeft: isSelected
                                ? "3px solid #1877f2"
                                : "3px solid transparent",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                            onClick={() => this.handleSelectPost(post)}
                          >
                            <div className="mb-2">
                              <strong
                                style={{
                                  fontSize: 13,
                                  wordBreak: "break-word",
                                }}
                              >
                                {previewText}
                              </strong>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-1">
                              <small className="text-muted">
                                {moment(post.created_time).format(
                                  "DD/MM/YYYY HH:mm",
                                )}
                              </small>
                              <div className="d-flex" style={{ gap: 8 }}>
                                <small className="text-muted" title="Likes">
                                  <i className="simple-icon-like mr-1" />
                                  {post.like_count || 0}
                                </small>
                                <small className="text-muted" title="Comments">
                                  <i className="simple-icon-bubbles mr-1" />
                                  {post.comment_count || 0}
                                </small>
                                <small className="text-muted" title="Shares">
                                  <i className="simple-icon-share mr-1" />
                                  {post.share_count || 0}
                                </small>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </PerfectScrollbar>
                )}
              </div>
            </CardBody>
          </Card>
        </Colxx>

        {/* Right: Comment thread */}
        <Colxx xxs="12" md="7" lg="8">
          <Card className="h-100">
            <CardBody
              className="px-3 py-0 d-flex flex-column"
              style={{ height: "75vh" }}
            >
              {selectedPost ? (
                <>
                  {/* Post Header */}
                  <div className="p-3 border-bottom">
                    <div className="d-flex align-items-start justify-content-between">
                      <div className="flex-grow-1">
                        <h6
                          className="mb-1 font-weight-bold"
                          style={{ fontSize: 14 }}
                        >
                          <i className="simple-icon-note mr-2 text-primary" />
                          <IntlMessages id="fb.comment.post-detail" />
                        </h6>
                        <p className="mb-0 text-muted" style={{ fontSize: 13 }}>
                          {selectedPost.message
                            ? selectedPost.message.length > 200
                              ? selectedPost.message.substring(0, 200) + "..."
                              : selectedPost.message
                            : "[Media Post]"}
                        </p>
                        <small className="text-muted">
                          {moment(selectedPost.created_time).format(
                            "DD/MM/YYYY HH:mm",
                          )}
                          {selectedPost.permalink_url && (
                            <a
                              href={selectedPost.permalink_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2"
                            >
                              <i className="simple-icon-link mr-1" />
                              <IntlMessages id="fb.comment.view-on-fb" />
                            </a>
                          )}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="flex-grow-1 position-relative overflow-hidden">
                    <PerfectScrollbar
                      className="h-100 p-3"
                      options={{ suppressScrollX: true }}
                    >
                      {loadingComments ? (
                        <div className="d-flex flex-column h-100 align-items-center justify-content-center">
                          <div
                            className="loading position-relative"
                            style={{ height: 20, width: 20, margin: "0 auto" }}
                          />
                          <p
                            className="text-muted mt-3"
                            style={{ fontSize: 13 }}
                          >
                            <IntlMessages id="fb.comment.loading-comments" />
                          </p>
                        </div>
                      ) : comments.length === 0 ? (
                        <div className="d-flex flex-column h-100 align-items-center justify-content-center text-muted">
                          <i
                            className="simple-icon-bubbles mb-3"
                            style={{ fontSize: 40, opacity: 0.3 }}
                          />
                          <p>
                            <IntlMessages id="fb.comment.no-comments" />
                          </p>
                        </div>
                      ) : (
                        this.renderCommentTree(comments)
                      )}
                    </PerfectScrollbar>
                  </div>
                </>
              ) : (
                <div className="d-flex flex-column h-100 align-items-center justify-content-center text-muted">
                  <i
                    className="simple-icon-bubbles mb-3"
                    style={{ fontSize: 60, opacity: 0.3 }}
                  />
                  <p>
                    <IntlMessages id="fb.comment.select-post" />
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </Colxx>
      </Row>
    );
  }
}

export default injectIntl(FacebookCommentApp);
